<script setup lang="ts">
import MorphText from '@/components/assistant/chat/message/MorphText.vue'
import type {MessageProgress} from '@/client/mentor/MessagesClient'
import {MessageProgressPhase} from '@/client/mentor/MessagesClient'
import {computed} from 'vue'
import {useI18n} from 'vue-i18n'

const PHASE_KEY_MAPPINGS: Record<MessageProgressPhase, string> = {
  [MessageProgressPhase.CREATE_MESSAGE]: 'assistant.chat.message.pending.phase.createMessage',
  [MessageProgressPhase.SCHEDULE_MESSAGE]: 'assistant.chat.message.pending.phase.scheduleMessage',
  [MessageProgressPhase.CALLING_AGENT]: 'assistant.chat.message.pending.phase.callingAgent',
  [MessageProgressPhase.PROCESS_TOOL]: 'assistant.chat.message.pending.phase.processTool',
  [MessageProgressPhase.SYNTHESIZING_RESPONSE]: 'assistant.chat.message.pending.phase.synthesizingResponse',
}

const TOOL_NAME_MAPPINGS: Record<string, string> = {
  getCurrentDateTime: 'assistant.chat.message.pending.tool.getCurrentDateTime',
  getShiftedDateOnSameDay: 'assistant.chat.message.pending.tool.getShiftedDateOnSameDay',
  getShiftedDateOnSameWeekday: 'assistant.chat.message.pending.tool.getShiftedDateOnSameWeekday',
  getShiftedDatePeriod: 'assistant.chat.message.pending.tool.getShiftedDatePeriod',
  resolveObservationDate: 'assistant.chat.message.pending.tool.resolveObservationDate',
  getEvents: 'assistant.chat.message.pending.tool.getEvents',
  getForecasterCompany: 'assistant.chat.message.pending.tool.getForecasterCompany',
  getOccupancyForecast: 'assistant.chat.message.pending.tool.getOccupancyForecast',
  getRegionForecast: 'assistant.chat.message.pending.tool.getRegionForecast',
  getRevenueForecast: 'assistant.chat.message.pending.tool.getRevenueForecast',
  getCapacity: 'assistant.chat.message.pending.tool.getCapacity',
  getCategoryNames: 'assistant.chat.message.pending.tool.getCategoryNames',
  getClosedDays: 'assistant.chat.message.pending.tool.getClosedDays',
  getRoomTypeNames: 'assistant.chat.message.pending.tool.getRoomTypeNames',
  getFullMessageContent: 'assistant.chat.message.pending.tool.getFullMessageContent',
  getLastSentRates: 'assistant.chat.message.pending.tool.getLastSentRates',
  getRecommendedRates: 'assistant.chat.message.pending.tool.getRecommendedRates',
  getRatesToConfirm: 'assistant.chat.message.pending.tool.getRatesToConfirm',
  getFilterValues: 'assistant.chat.message.pending.tool.getFilterValues',
  getAnalyseStats: 'assistant.chat.message.pending.tool.getAnalyseStats',
  getPickupStats: 'assistant.chat.message.pending.tool.getPickupStats',
  getPickupDevelopmentStats: 'assistant.chat.message.pending.tool.getPickupDevelopmentStats',
  getMarketOccupancyComparisonStats: 'assistant.chat.message.pending.tool.getMarketOccupancyComparisonStats',
  getMarketPickupComparisonStats: 'assistant.chat.message.pending.tool.getMarketPickupComparisonStats',
  getCurrentWeather: 'assistant.chat.message.pending.tool.getCurrentWeather',
  getFutureWeather: 'assistant.chat.message.pending.tool.getFutureWeather',
  citeKnowledgeSource: 'assistant.chat.message.pending.tool.citeKnowledgeSource',
  loadFullSkill: 'assistant.chat.message.pending.tool.loadFullSkill',
}

const {t, te} = useI18n()

const props = defineProps<{
  progress: MessageProgress
}>()

const lastProgressKey = computed(() => {
  if (props.progress.phase === MessageProgressPhase.PROCESS_TOOL && props.progress.toolName) {
    const toolNameKey = TOOL_NAME_MAPPINGS[props.progress.toolName]
    if (te(toolNameKey)) {
      return t(toolNameKey)
    }
  }

  if (props.progress.phase) {
    const phaseKey = PHASE_KEY_MAPPINGS[props.progress.phase]
    if (te(phaseKey)) {
      return t(phaseKey)
    }
  }

  return undefined
})
</script>

<template>
  <div class="text-xs text-new-neutral-600 flex flex-row items-center gap-1">
    <i class="fa-solid fa-spinner fa-spin -mt-0.5" />
    <MorphText v-if="lastProgressKey" :text="lastProgressKey" />
  </div>
</template>
